// @flow

// Libs
import React, { Component } from 'react'
import moment from 'moment'
import nAf from 'fc-utils/nAf'

// Components
import TextInput from './TextInput'
import DateTimeComponent from './DateTime'
import ClearDateButton from './ClearDateButton'

// Utils
import withViewport from 'hocs/withViewport'

// Styles
import {
  datePickerPair,
  dateTimeWrapper,
  dateTimeStyle,
  hoverDateStyle,
  selectedDateStyle,
  unselectedDateStyle,
  firstLastDateStyle,
  arrowLeft,
  arrowRight,
  dayAfterStyle,
  dayBeforeStyle,
  disabledDateStyle,
  dateTimeInputWrapper,
  dateTimeRangeInputFocusStyle,
} from '../styles'

import './../css/datetime.css'

// Constants
import {
  UPDATE_START,
  UPDATE_END,
  DATE_FORMAT,
  CLEAR_START_END_DATES,
} from './../constants'

// Type
type Props = {
  required: boolean,
  isValid: boolean,
  start: ?string,
  end: ?string,
  hideLabel: ?boolean,
  isVertical: boolean,
  minLimit: ?number,
  maxLimit: ?number,
  defaultValueStart: ?Object,
  defaultValueEnd: ?Object,
  onChange: Function,
  viewport: Object,
}

// Exports
class DateRangeSelector extends Component {
  props: Props
  handleDocumentClick: Function
  onChange: Function
  onFocusStart: Function
  onFocusEnd: Function
  onBlur: Function
  onHover: Function
  isValidDate: Function
  renderDay: Function
  clearInput: Function
  reset: Function

  constructor (props: Props) {
    super(props)

    this.state = {
      toggled: false,
      turn: UPDATE_START,
      start: props.start,
      end: props.end,
      errors: null,
    }

    this.onFocusStart = this.onFocusStart.bind(this)
    this.onFocusEnd = this.onFocusEnd.bind(this)
    this.onBlur = this.onBlur.bind(this)
    this.renderDay = this.renderDay.bind(this)
    this.onHover = this.onHover.bind(this)
    this.onChange = this.onChange.bind(this)
    this.handleDocumentClick = this.handleDocumentClick.bind(this)
    this.isValidDate = this.isValidDate.bind(this)
    this.clearInput = this.clearInput.bind(this)
    this.reset = this.reset.bind(this)
  }

  componentDidMount () {
    // to close outside of picker
    window.__root_container.addEventListener('click', this.handleDocumentClick)
  }

  componentWillUnmount (): * {
    window.__root_container.removeEventListener('click', this.handleDocumentClick)
  }

  handleDocumentClick<Event> (evt: Event): * {
    const DateRangeSelectorStart = this.refs.DateRangeSelectorStart
    const DateRangeSelectorEnd = this.refs.DateRangeSelectorEnd
    const DateRangeSelector = this.refs.DateRangeSelector
    if (this.state.toggled &&
      !DateRangeSelectorStart.contains(evt.target) &&
      !DateRangeSelectorEnd.contains(evt.target) &&
      !DateRangeSelector.contains(evt.target)) {
      setTimeout(this.onBlur, 250)
    }
  }

  onFocusStart<Event> (event: Event): * {
    this.setState({
      toggled: true,
      start: null,
      end: null,
      turn: UPDATE_START,
    })
    event.target.blur()
  }

  onFocusEnd<Event> (event: Event): * {
    const { start } = this.props
    this.setState({
      toggled: true,
      start: start,
      end: null,
      turn: UPDATE_END,
    })
    event.target.blur()
  }

  onBlur () {
    const isEmpty = !this.state.start && !this.state.end
    const isComplete = this.state.start && this.state.end
    if (isEmpty || isComplete) {
      this.setState({
        toggled: false,
      })
    }
  }

  onHover (date: Object): * {
    this.setState({hoverDate: date})
  }

  renderDay (props: Props, currentDate: Object, selectedDate: Object): * {
    let { start: startProp, end: endProp, minLimit, maxLimit } = this.props
    const { hoverDate } = this.state
    let { start, end } = this.state

    startProp = moment(moment(startProp).format(DATE_FORMAT))
    endProp = moment(moment(endProp).format(DATE_FORMAT))
    start = moment(moment(start).format(DATE_FORMAT))
    end = moment(moment(end).format(DATE_FORMAT))
    currentDate = moment(moment(currentDate).format(DATE_FORMAT))

    const isPrevValue = (!start.isValid() && moment(startProp).isSame(moment(currentDate))) ||
     (!end.isValid() && moment(endProp).isSame(moment(currentDate)))

    const selected = (moment(start).isBefore(moment(currentDate)) ||
      moment(start).isSame(moment(currentDate))) &&
      (moment(end).isAfter(moment(currentDate)) ||
      moment(end).isSame(moment(currentDate)))

    const hover = ((!moment(end).isValid() && moment(start).isValid() &&
      moment(start).isBefore(moment(currentDate))) &&
      (moment(hoverDate).isAfter(moment(currentDate)) ||
      moment(hoverDate).isSame(moment(currentDate)))) ||
      ((!moment(start).isValid() && moment(end).isValid() &&
        moment(end).isAfter(moment(currentDate))) &&
        (moment(hoverDate).isBefore(moment(currentDate)) ||
        moment(hoverDate).isSame(moment(currentDate))))

    const isStartEnd = moment(start).isSame(moment(currentDate)) ||
      moment(end).isSame(moment(currentDate))

    const isDayAfterStart = moment(start).add('day', 1).isSame(moment(currentDate))

    const isDayBeforeEnd = moment(end).subtract('day', 1).isSame(moment(currentDate))

    const isDayAfterStartPrev = moment(startProp).add('day', 1).isSame(moment(currentDate))

    const isDayBeforeEndPrev = moment(endProp).subtract('day', 1).isSame(moment(currentDate))

    const isDisabled = (moment(start).isAfter(currentDate) && !moment(end).isValid()) ||
      (moment(end).isBefore(currentDate) && !moment(start).isValid()) ||
      (minLimit && moment(minLimit).isAfter(currentDate)) ||
      (maxLimit && moment(maxLimit).isBefore(currentDate))

    return (
      <td {...props}
        onMouseEnter={() => { this.onHover(currentDate) }}
        style={{
          ...(isDisabled ? disabledDateStyle
            : (isStartEnd ? firstLastDateStyle
              : (selected ? selectedDateStyle()
                : (hover ? hoverDateStyle : unselectedDateStyle)))),
          ...((isDayBeforeEnd && !isDayAfterStart) || (isDayBeforeEndPrev && !isDayAfterStartPrev)
            ? dayBeforeStyle
            : ((isDayAfterStart && !isDayBeforeEnd) || (isDayAfterStartPrev && !isDayBeforeEndPrev)
              ? dayAfterStyle
              : {})),
          ...(isPrevValue ? selectedDateStyle({isGrey: true}) : {}),
        }}>
        {isDayAfterStart && !(isDayAfterStart && isDayBeforeEnd)
          ? <div style={arrowRight()} />
          : isDayAfterStartPrev && !(isDayAfterStartPrev && isDayBeforeEndPrev)
            ? <div style={arrowRight({isGrey: true})} />
            : null
        }
        {currentDate.date()}
        {isDayBeforeEnd && !(isDayAfterStart && isDayBeforeEnd)
          ? <div style={arrowLeft()} />
          : isDayBeforeEndPrev && !(isDayAfterStartPrev && isDayBeforeEndPrev)
            ? <div style={arrowLeft({isGrey: true})} />
            : null
        }
      </td>)
  }

  onChange (date: Object): * {
    const { turn } = this.state
    const { onChange, maxLimit } = this.props
    let { start, end } = this.props
    if (turn === UPDATE_START) {
      if (moment(end).isBefore(date)) {
        end = maxLimit
      }
      if (moment(start).isValid() && moment(end).isValid()) {
        this.setState({start: date || null, end: null})
        onChange({start: date || null, end: end})
        this.setState({turn: UPDATE_END})
      } else {
        this.setState({start: date || null, end: null, turn: UPDATE_END})
        onChange({start: date || null, end: end})
      }
      if (!moment(end).isValid()) {
        this.setState({turn: UPDATE_END})
      }
    } else if (turn === UPDATE_END) {
      this.setState({start: start || null, end: date || null})
      onChange({start: start || null, end: date || null})
      this.setState({turn: UPDATE_START})
      setTimeout(this.onBlur, 250)
    }
    return false
  }

  isValidDate (currentDate: Object, selectedDate: Object): * {
    const { start, end, minLimit, maxLimit } = this.props
    const { start: start_state } = this.state
    if (minLimit && moment(minLimit).isAfter(currentDate)) {
      return false
    }
    if (start_state && moment(start_state).isSameOrAfter(currentDate)) {
      return false
    }
    if (maxLimit && moment(maxLimit).isBefore(currentDate)) {
      return false
    }
    if (moment(start).isValid() && !moment(end).isValid()) {
      return moment(currentDate).isValid() && moment(currentDate).isAfter(moment(start))
    } else if (!moment(start).isValid() && moment(end).isValid()) {
      // Due to a bug we couldn't fix we commented this line.
      // Step to reproduce the bug
      // Select start and end date from the first calendar (It's important to select both from the first)
      // click on the start textInput and then click on "Clear Start"
      // The application will hang if the line bellow is uncommented

      // return moment(currentDate).isValid() && moment(currentDate).isAfter(moment(start))


      // The workaround for this issue was to return true here (which means all the dates are valid) and
      // then if the user selects a start date after the end date, then we clear the end date and user should
      // select another end date
      // Hossein Talebi, Sept. 8, 2017
      return true
    }

    return true
  }

  clearInput (e: Event, TURN: string): * {
    e.stopPropagation()
    const { start, end, onChange } = this.props
    if (TURN === UPDATE_END) {
      if (moment(start).isValid()) {
        this.setState({turn: UPDATE_END, end: null})
      }
      onChange({start: start, end: null})
    } else if (TURN === UPDATE_START) {
      this.setState({turn: UPDATE_START, start: null})
      onChange({start: null, end: end})
    } else if (TURN === CLEAR_START_END_DATES) {
      this.reset()
    }
  }

  reset () {
    this.setState({turn: UPDATE_START})
    this.props.onChange({start: '', end: ''})
  }

  render (): React.Element<any> {
    const {
      start,
      end,
      required,
      isValid,
      viewport,
      defaultValueStart,
      defaultValueEnd,
      hideLabel,
    } = this.props
    const { toggled, turn } = this.state
    const isVertical = this.props.isVertical || viewport.width < 768
    return (
      <div
        data-test='DateRangeSelector'
        style={datePickerPair(viewport, isVertical)}
        ref='DateRangeSelector'>
        <div style={dateTimeWrapper(viewport, isVertical)}>
          <div style={dateTimeInputWrapper(this.state.start)}>
            <TextInput
              label={'Start Date'}
              placeholder={'Start Date'}
              onFocus={this.onFocusStart}
              required={required}
              isValid={isValid}
              value={(moment(start).isValid() && moment(start).format(DATE_FORMAT)) || ''}
              focusStyle={dateTimeRangeInputFocusStyle(toggled && turn === UPDATE_START)}
              hidden={false}
              hideLabel={hideLabel}
              // errors={errors}
            />
            <div style={dateTimeStyle('startDatePicker', isVertical)} ref='DateRangeSelectorStart'>
              <DateTimeComponent
                key='startDate'
                input={false}
                open={toggled}
                timeFormat={false}
                closeOnSelect
                onClickOutside={nAf}
                onChange={this.onChange}
                renderDay={this.renderDay}
                isValidDate={this.isValidDate}
                defaultValue={moment(defaultValueStart)}
              />
              <ClearDateButton
                text={isVertical ? 'Clear Dates' : 'Clear Start'}
                isOpen={toggled}
                onClick={(e: Event): * => this.clearInput(e, isVertical ? CLEAR_START_END_DATES : UPDATE_START)}
              />
            </div>
          </div>
        </div>
        <div style={dateTimeWrapper(viewport, isVertical)}>
          <div style={dateTimeInputWrapper(this.state.end)}>
            <TextInput
              label={'End Date'}
              placeholder={'End Date'}
              onFocus={this.onFocusEnd}
              required={required}
              isValid={isValid}
              value={(moment(end).isValid() && moment(end).format(DATE_FORMAT)) || ''}
              focusStyle={dateTimeRangeInputFocusStyle(toggled && turn === UPDATE_END)}
              hidden={false}
              hideLabel={hideLabel}
              // errors={errors}
            />
            <div style={dateTimeStyle('endDatePicker', isVertical)} ref='DateRangeSelectorEnd'>
              <DateTimeComponent
                input={false}
                key='endDate'
                open={toggled}
                timeFormat={false}
                closeOnSelect
                onClickOutside={nAf}
                onChange={this.onChange}
                renderDay={this.renderDay}
                defaultValue={moment(defaultValueEnd)}
                isValidDate={this.isValidDate}
              />
              <ClearDateButton
                text='Clear End'
                isOpen={toggled}
                onClick={(e: Event): * => this.clearInput(e, UPDATE_END)}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }
}

DateRangeSelector.defaultProps = {
  required: false,
  isValid: true,
  dateOnly: false,
  timeOnly: false,
  start: null,
  end: null,
  isVertical: false,
  minLimit: null,
  maxLimit: null,
  defaultValueStart: moment().subtract(1, 'month'),
  defaultValueEnd: moment(),
}

export default withViewport(DateRangeSelector)
